
import React from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const GameGuide = () => {
  return (
    <section id="game-guide" className="py-10">
      <div className="bg-indigo-800 bg-opacity-50 rounded-xl p-8 shadow-xl">
        <h2 className="text-3xl font-bold mb-6 text-center text-yellow-300">Capybara Clicker 3D Game Guide</h2>
        
        <div className="space-y-6 text-indigo-100">
          <p className="text-lg leading-relaxed">
            Welcome to our comprehensive guide for <span className="font-semibold text-pink-300">Capybara Clicker 3D</span>! Whether you're just starting your capybara-clicking journey or looking to optimize your strategy, this guide will help you build the most impressive capybara empire possible in this free online game.
          </p>
          
          <h3 className="text-2xl font-bold mt-8 mb-4 text-pink-300">Getting Started</h3>
          
          <p className="text-lg leading-relaxed">
            When you first launch <span className="font-semibold text-pink-300">Capybara Clicker 3D</span>, you'll be greeted by a single capybara in a basic environment. Your primary goal is to increase your capybara population by clicking on the screen. Each click represents your effort to breed and multiply your capybaras.
          </p>
          
          <div className="bg-purple-800 bg-opacity-40 p-6 rounded-lg my-6">
            <h4 className="text-xl font-bold mb-3 text-yellow-200">Basic Controls:</h4>
            <ul className="list-disc pl-6 space-y-2">
              <li><span className="font-semibold">Left Mouse Button</span>: Click to increase your capybara count</li>
              <li><span className="font-semibold">Mouse Movement</span>: Navigate around the 3D environment</li>
              <li><span className="font-semibold">Scroll Wheel</span>: Zoom in and out</li>
              <li><span className="font-semibold">Menu Buttons</span>: Access game features like upgrades, customization, and settings</li>
            </ul>
          </div>
          
          <p className="text-lg leading-relaxed">
            The first few minutes of gameplay are straightforward – simply click as much as you can to build up your initial capybara population. As you reach certain milestones, you'll unlock additional features that expand your gameplay options.
          </p>
          
          <h3 className="text-2xl font-bold mt-8 mb-4 text-pink-300">Understanding the Upgrade System</h3>
          
          <p className="text-lg leading-relaxed">
            The key to success in <span className="font-semibold text-pink-300">Capybara Clicker 3D</span> is effectively using the upgrade system. As you accumulate capybaras, you'll earn currency that can be spent on various upgrades. These upgrades fall into several categories:
          </p>
          
          <Accordion type="single" collapsible className="w-full mt-4">
            <AccordionItem value="item-1" className="border-b border-purple-700">
              <AccordionTrigger className="text-lg font-semibold text-yellow-200 hover:text-yellow-100 py-4">Click Multipliers</AccordionTrigger>
              <AccordionContent className="text-indigo-100 pb-4">
                <p>Click Multipliers increase the value of each click you make. For example, a 2x Click Multiplier means each click generates twice as many capybaras. These upgrades are essential for making your manual clicking more efficient.</p>
                <p className="mt-2">Strategy tip: Prioritize Click Multipliers early in the game when you're actively clicking. They provide the most immediate boost to your capybara production.</p>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-2" className="border-b border-purple-700">
              <AccordionTrigger className="text-lg font-semibold text-yellow-200 hover:text-yellow-100 py-4">Auto-Clickers</AccordionTrigger>
              <AccordionContent className="text-indigo-100 pb-4">
                <p>Auto-Clickers generate capybaras automatically over time without requiring manual clicks. Each Auto-Clicker produces a certain number of capybaras per second.</p>
                <p className="mt-2">Strategy tip: Invest in Auto-Clickers when you want to make progress even when you're not actively playing. They're essential for long-term growth.</p>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-3" className="border-b border-purple-700">
              <AccordionTrigger className="text-lg font-semibold text-yellow-200 hover:text-yellow-100 py-4">Capybara Enhancements</AccordionTrigger>
              <AccordionContent className="text-indigo-100 pb-4">
                <p>Capybara Enhancements improve various aspects of your capybaras, such as breeding efficiency, lifespan, and special abilities. These upgrades often provide percentage-based increases to your overall production.</p>
                <p className="mt-2">Strategy tip: Balance your investment in Capybara Enhancements with other upgrades for optimal growth. Some enhancements have synergistic effects when combined.</p>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-4" className="border-b border-purple-700">
              <AccordionTrigger className="text-lg font-semibold text-yellow-200 hover:text-yellow-100 py-4">Environmental Upgrades</AccordionTrigger>
              <AccordionContent className="text-indigo-100 pb-4">
                <p>Environmental Upgrades modify the habitat where your capybaras live. These upgrades can change the weather, terrain, and background elements, often providing both visual improvements and gameplay bonuses.</p>
                <p className="mt-2">Strategy tip: Don't underestimate Environmental Upgrades – some provide substantial bonuses to specific aspects of production. Read the descriptions carefully to understand their benefits.</p>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-5" className="border-b border-purple-700">
              <AccordionTrigger className="text-lg font-semibold text-yellow-200 hover:text-yellow-100 py-4">Special Abilities</AccordionTrigger>
              <AccordionContent className="text-indigo-100 pb-4">
                <p>Special Abilities are powerful active skills that you can trigger manually to provide temporary but significant boosts. These might include rapid breeding frenzies, temporary multipliers, or resource bonuses.</p>
                <p className="mt-2">Strategy tip: Save Special Abilities for when you're actively playing and can make the most of their effects. Combine multiple abilities for even greater benefits.</p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
          
          <h3 className="text-2xl font-bold mt-8 mb-4 text-pink-300">Customization Guide</h3>
          
          <p className="text-lg leading-relaxed">
            <span className="font-semibold text-pink-300">Capybara Clicker 3D</span> offers extensive customization options that allow you to personalize your capybaras. Beyond just being decorative, many customization items provide gameplay benefits:
          </p>
          
          <div className="bg-purple-800 bg-opacity-40 p-6 rounded-lg my-6">
            <h4 className="text-xl font-bold mb-3 text-yellow-200">Customization Categories:</h4>
            <ul className="list-disc pl-6 space-y-3">
              <li><span className="font-semibold text-pink-200">Hats and Headwear</span>: From tiny top hats to flower crowns, headwear can increase your click value or provide passive income bonuses.</li>
              <li><span className="font-semibold text-pink-200">Body Accessories</span>: Items like scarves, backpacks, and vests can boost your capybaras' production efficiency.</li>
              <li><span className="font-semibold text-pink-200">Color Patterns</span>: Changing your capybaras' fur patterns and colors can unlock special abilities or boost specific upgrade effects.</li>
              <li><span className="font-semibold text-pink-200">Environment Decorations</span>: Personalize the habitat with decorative elements that can also provide bonuses to nearby capybaras.</li>
            </ul>
          </div>
          
          <p className="text-lg leading-relaxed">
            Experiment with different combinations of customizations to find the most beneficial setup for your gameplay style. Some items work particularly well together, creating synergistic effects that can significantly boost your production.
          </p>
          
          <h3 className="text-2xl font-bold mt-8 mb-4 text-pink-300">Advanced Strategies</h3>
          
          <p className="text-lg leading-relaxed">
            Once you've mastered the basics of <span className="font-semibold text-pink-300">Capybara Clicker 3D</span>, consider these advanced strategies to maximize your capybara empire:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
            <div className="bg-purple-900 bg-opacity-40 p-5 rounded-lg border border-purple-700">
              <h4 className="text-lg font-bold mb-2 text-yellow-200">Balanced Upgrade Approach</h4>
              <p>Rather than focusing all your resources on one type of upgrade, maintain a balanced approach. Invest in both click multipliers and auto-clickers to ensure steady progress whether you're actively playing or away.</p>
            </div>
            
            <div className="bg-purple-900 bg-opacity-40 p-5 rounded-lg border border-purple-700">
              <h4 className="text-lg font-bold mb-2 text-yellow-200">Strategic Saving</h4>
              <p>Sometimes it's better to save your resources for more expensive upgrades rather than immediately spending on smaller improvements. Calculate the cost-to-benefit ratio of different upgrades to make informed decisions.</p>
            </div>
            
            <div className="bg-purple-900 bg-opacity-40 p-5 rounded-lg border border-purple-700">
              <h4 className="text-lg font-bold mb-2 text-yellow-200">Achievement Hunting</h4>
              <p>Actively pursue achievements, as they often provide substantial one-time bonuses that can accelerate your progress. Review the achievement list regularly to identify easily attainable goals.</p>
            </div>
            
            <div className="bg-purple-900 bg-opacity-40 p-5 rounded-lg border border-purple-700">
              <h4 className="text-lg font-bold mb-2 text-yellow-200">Special Event Participation</h4>
              <p>Whenever special events occur, prioritize participation to earn limited-time rewards. These exclusive items and bonuses are often more powerful than regular upgrades.</p>
            </div>
          </div>
          
          <h3 className="text-2xl font-bold mt-8 mb-4 text-pink-300">Troubleshooting</h3>
          
          <p className="text-lg leading-relaxed">
            If you encounter any issues while playing <span className="font-semibold text-pink-300">Capybara Clicker 3D</span>, here are some common troubleshooting steps:
          </p>
          
          <ul className="list-disc pl-8 space-y-2 mt-4">
            <li>If the game is running slowly, try lowering the graphics quality in the settings menu.</li>
            <li>Make sure your browser is updated to the latest version for optimal performance.</li>
            <li>Clear your browser cache if you notice any visual glitches or loading issues.</li>
            <li>Disable any browser extensions that might interfere with the game's functionality.</li>
            <li>If the game becomes unresponsive, refreshing the page usually resolves the issue.</li>
          </ul>
          
          <p className="text-lg leading-relaxed mt-6">
            With this guide, you're well-equipped to build an impressive capybara empire in <span className="font-semibold text-pink-300">Capybara Clicker 3D</span>. Remember that the key to success is consistent clicking, strategic upgrading, and having fun with your growing collection of adorable capybaras!
          </p>
        </div>
      </div>
    </section>
  );
};

export default GameGuide;
